"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { AlertTriangle, ArrowRight } from "lucide-react";

import { useSession } from "@/hooks/useSession";
import { formatMoney } from "@/lib/format-money";

const TONE_BY_STATUS: Record<string, string> = {
  grace: "border-sky-500/30 bg-sky-500/10 text-sky-100",
  warning: "border-yellow-500/30 bg-yellow-500/10 text-yellow-100",
  restricted: "border-orange-500/30 bg-orange-500/10 text-orange-100",
};

export function TenantBillingAccessBanner() {
  const pathname = usePathname();
  const session = useSession();
  const access = session.tenantBillingAccess;

  if (session.status !== "ready" || session.role === "SUPER_ADMIN" || !access) {
    return null;
  }

  if (access.billingOnly || access.status === "active") {
    return null;
  }

  if (!(pathname ?? "").startsWith("/app")) {
    return null;
  }

  const tone = TONE_BY_STATUS[access.status] ?? TONE_BY_STATUS.warning;
  const outstanding = formatMoney(
    access.outstandingAmount ?? 0,
    access.currency ?? session.defaultBillingCurrency,
  );

  return (
    <div className={`border-b px-4 py-3 ${tone}`}>
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium">
              Billing status: {access.status}. Outstanding balance {outstanding}.
            </p>
            <p className="opacity-90">
              {access.customerMessage ??
                "Please review billing to avoid additional service restrictions."}
            </p>
          </div>
        </div>
        <Link
          href="/app?tab=billing"
          className="inline-flex items-center rounded-md bg-white/10 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-white/20"
        >
          Review Billing
          <ArrowRight className="ml-2 h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
